import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

import {
  recordedStudentSessionIssues,
  SessionFeedbackIssue,
  sessionFeedbackIssueSchema,
} from './session-feedback-issue.js';
import { toSessionFeedbackCompoundId } from './session-feedbacks.js';
import { StudentFeedbackIssue, StudentFeedbackStatus } from './student-feedback-shared.js';
import { studentFeedbackSchema } from './student-feedbacks.js';
import { userSchema } from './user.js';
import { paginatedResponse } from './util-schema-types.js';

export const CLASS_FEEDBACK_REVIEW_TYPE_LABEL = 'Class feedback';

export const classFeedbackReviewNoteSchema = z.object({
  kind: z.literal('class_feedback'),
  id: z.string(),
  studentId: z.string(),
  sessionId: z.string(),
  coachId: z.string(),
  dateInstant: z.instanceof(Temporal.Instant),
  reviewer: userSchema,
  dailySummary: z.string().nullable(),
  studentAttended: z.boolean().nullable(),
  studentIssues: z.array(sessionFeedbackIssueSchema),
  classNotes: z.string().nullable(),
});
export type ClassFeedbackReviewNote = z.infer<typeof classFeedbackReviewNoteSchema>;

export const studentFeedbackReviewNoteSchema = z.object({
  kind: z.literal('student_feedback'),
  feedback: studentFeedbackSchema,
});
export type StudentFeedbackReviewNote = z.infer<typeof studentFeedbackReviewNoteSchema>;

export const studentReviewNoteSchema = z.discriminatedUnion('kind', [
  studentFeedbackReviewNoteSchema,
  classFeedbackReviewNoteSchema,
]);
export type StudentReviewNote = z.infer<typeof studentReviewNoteSchema>;

export const getStudentReviewNotesResultsSchema = paginatedResponse.extend({
  reviewNotes: z.array(studentReviewNoteSchema),
});
export type GetStudentReviewNotesResults = z.infer<typeof getStudentReviewNotesResultsSchema>;

export interface ReviewNotePageKey {
  kind: StudentReviewNote['kind'];
  id: string;
  dateInstant: Temporal.Instant;
}

export interface ClassFeedbackReviewNoteInput {
  sessionId: string;
  studentId: string;
  coachId: string;
  dateInstant: Temporal.Instant;
  reviewer: ClassFeedbackReviewNote['reviewer'];
  dailySummary: string | null;
  studentAttended: boolean | null;
  issues: readonly SessionFeedbackIssue[];
  classNotes: string | null;
}

/** Blank or whitespace-only class notes were not recorded. */
export function recordedClassNotes(classNotes: string | null | undefined): string | null {
  if (classNotes == null) return null;
  return classNotes.trim() ? classNotes : null;
}

/**
 * Map one saved class-feedback row onto a review note.
 * Reviewer is the session teacher stored on that feedback (the substitute, when one taught).
 * Student issues are only the issues recorded for this student; other feedback issues are omitted.
 */
export function toClassFeedbackReviewNote(input: ClassFeedbackReviewNoteInput): ClassFeedbackReviewNote {
  return {
    kind: 'class_feedback',
    id: toSessionFeedbackCompoundId({
      sessionId: input.sessionId,
      studentId: input.studentId,
      coachId: input.coachId,
    }),
    studentId: input.studentId,
    sessionId: input.sessionId,
    coachId: input.coachId,
    dateInstant: input.dateInstant,
    reviewer: input.reviewer,
    dailySummary: input.dailySummary,
    studentAttended: input.studentAttended,
    studentIssues: recordedStudentSessionIssues(input.issues),
    classNotes: recordedClassNotes(input.classNotes),
  };
}

/**
 * Class feedback is a separate review type. Hide it when the admin filters to an existing
 * review type or issue status, which class feedback does not have.
 */
export function includeClassFeedbackReviewNotes(filters?: {
  studentFeedbackIssues?: readonly StudentFeedbackIssue[];
  statuses?: readonly StudentFeedbackStatus[];
}): boolean {
  const issueCount = filters?.studentFeedbackIssues?.length ?? 0;
  const statusCount = filters?.statuses?.length ?? 0;
  return issueCount === 0 && statusCount === 0;
}

/**
 * Merge review-note keys onto one date order and return the requested page.
 * Equal timestamps keep student feedback ahead of class feedback, and otherwise keep input order.
 */
export function pageReviewNoteKeys<T extends ReviewNotePageKey>(
  keys: readonly T[],
  { pageIndex, pageSize, sortDescending }: { pageIndex: number; pageSize: number; sortDescending: boolean },
): { page: T[]; total: number } {
  const sorted = [...keys].sort((a, b) => {
    const byDate = Temporal.Instant.compare(a.dateInstant, b.dateInstant);
    if (byDate !== 0) return sortDescending ? -byDate : byDate;
    if (a.kind === b.kind) return 0;
    return a.kind === 'student_feedback' ? -1 : 1;
  });
  const start = pageIndex * pageSize;
  return { page: sorted.slice(start, start + pageSize), total: sorted.length };
}
